import { useBundleRegistration } from './bundleContext';
import { useCommerce } from '../../commerce/ShopCommerce';
import React, { useEffect, useState } from 'react';
import { previewShowsSamples } from '../../api/previewVisit';
import { getProductBySlug, type StorefrontProduct } from '../../api/client';
import { productFactsOf } from '../../themeHost/themeMount';
import { ProductContext, VariantContext, useProduct } from './productContext';
import { ElementRenderProvider, useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { useStoreRead } from './useStoreRead';
import { boolProp, textProp } from './elementProps';
import { blockProduct, sampleProducts } from './sampleCatalog';
import { placeholderProduct } from './placeholderCatalog';
import { useProductSlots } from './productSlots';
import { CatalogNote } from './catalogCards';
import { useAddressVariant } from './addressVariant';
import { factsForChoice, picturesForChoice } from './purchaseChoice';
import { orderable, statedStock } from '../../commerce/variantStock';

// Overrides just the product facts; store branding and currency continue to come from this store.
export const ProductScope: React.FC<{ product: StorefrontProduct; children: React.ReactNode; scopeId?: string }> = ({ product, children, scopeId }) => {
  const context = useElementRender();
  const register = useBundleRegistration();
  const [selected, select] = useAddressVariant(product);
  const [quantity, setQuantity] = useState(1);
  const variant = product.variants.find((row) => row.id === selected) ?? product.variants.find((row) => orderable(row)) ?? product.variants[0];
  useEffect(() => {
    if (!register || !scopeId || !variant) return;
    register(scopeId, { product, variantId: variant.id, quantity: 1 });
    return () => register(scopeId, null);
  }, [register, scopeId, product, variant?.id]);
  const chosen = selected !== '' && variant?.id === selected;
  const facts = productFactsOf(product, {
    unitPrice: Number(variant?.price ?? 0), compareAtPrice: variant?.compareAtPrice ? Number(variant.compareAtPrice) : null,
    stock: product.id.startsWith('sample-') ? null : statedStock(variant)
  }, chosen ? picturesForChoice(product, variant?.imageId) : product.images);
  // A card switched to one option shows that option's picture even under the mouse, and links to it.
  const opened = facts && chosen ? factsForChoice(facts, selected, product.id.startsWith('sample-')) : facts;
  const store = context.store ? { ...context.store, product: opened } : null;
  return <ProductContext.Provider value={product}><VariantContext.Provider value={{ selected: variant?.id ?? '', select, quantity, setQuantity, scoped: true }}><ElementRenderProvider value={{ ...context, store }}>
    {children}
  </ElementRenderProvider></VariantContext.Provider></ProductContext.Provider>;
};

/** The most positions a block may take a product from, matching the samples a theme may carry. */
const LAST_SLOT = 23;

// Reads the position a block takes its product from when none is chosen for it.
const slotOf = (props: Record<string, unknown>): number =>
  Math.max(0, Math.min(LAST_SLOT, Number(props.sampleIndex ?? new URLSearchParams(window.location.search).get('sample')) || 0));

// Reports whether the address itself names a product, which a block never overrides with one of its own.
export const addressNamesProduct = (): boolean =>
  window.location.pathname.startsWith('/products/') || new URLSearchParams(window.location.search).has('product');

// Reads the product this block's page gives it: the checkout's line on a checkout visit, else the page's product.
function useInheritedProduct(usePageProduct: boolean): StorefrontProduct | null {
  const pageProduct = useProduct();
  const commerce = useCommerce();
  if (!usePageProduct) return null;
  const checkoutVisit = new URLSearchParams(window.location.search).get('page') === 'checkout' || ['/cart', '/checkout'].includes(window.location.pathname);
  return (checkoutVisit ? commerce?.product ?? pageProduct : pageProduct) ?? null;
}

// Reads the theme sample a block may show at its position, when the theme gave it samples at all.
function sampleAt(props: Record<string, unknown>, slot: number): StorefrontProduct | null {
  if (!boolProp(props, 'sampleContent')) return null;
  return sampleProducts(props, LAST_SLOT + 1)[slot] ?? sampleProducts(props, 1)[0] ?? null;
}

/** The product one block draws, and whether it is still being read. */
type BlockPick = { product: StorefrontProduct | null; busy: boolean };

// Works out one block's product from its own pick, its page, the store's catalog, the theme's samples and the stand-ins.
function useBlockProduct(props: Record<string, unknown>): BlockPick {
  const inherited = useInheritedProduct(boolProp(props, 'usePageProduct', true));
  const handle = textProp(props, 'product');
  const read = useStoreRead(handle ? `product:${handle}` : null, () => getProductBySlug(handle));
  const free = !handle && !addressNamesProduct();
  const slots = useProductSlots(free && !inherited?.id);
  const slot = slotOf(props);
  const chosen = handle ? read.data : inherited;
  const product = blockProduct({
    chosen: chosen?.id ? chosen : null, free, own: slots.data, loaded: slots.status !== 'loading', slot,
    sample: sampleAt(props, slot), placeholder: placeholderProduct(slot), preview: previewShowsSamples()
  });
  return { product, busy: handle ? read.status === 'loading' : free && !product };
}

// Draws one product block: the product chosen for it, the page's, or the store's own at its position — the theme's
// sample in a preview on the shared demo store, and a stand-in where a store has nothing for it yet.
export const ProductScopeElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const pick = useBlockProduct(node.props);
  if (pick.busy) return <div {...box} aria-busy="true" />;
  if (!pick.product) return editing ? <div {...box}><CatalogNote>This product is no longer in your store. Choose another one.</CatalogNote></div> : null;
  return <div {...box} data-bundle-product={node.id}><ProductScope product={pick.product} scopeId={node.id}>{children}</ProductScope></div>;
};
